// Разборы актов выполненных работ: что делали, чем закрыли, что поставили.
// ТОЛЬКО ЧТЕНИЕ.
//
// ЧЕГО ЗДЕСЬ НЕТ И ПОЧЕМУ ЭТО ГЛАВНОЕ СВОЙСТВО ЭКРАНА
// В разборах лежит ФИО инженера, логин загрузившего и отработанные часы по датам с
// разбивкой день/вечер/ночь. Это тот же разряд данных, что овертаймы, и решение о их
// видимости за владельцем — оно ещё не принято. Поэтому раздел сделан работающим БЕЗ
// них: ядро (avr_api.py) не выбирает эти поля из базы вовсе, и экран не может их
// показать даже по ошибке — их нет в ответе. Оговорка вверху говорит об этом прямо, а не
// оставляет человека гадать, потеряли данные или спрятали.
//
// ПОЧЕМУ ТРАНСПОРТ ЛЕЖИТ В ЭТОМ ЖЕ ФАЙЛЕ
// Обычно у раздела свой src/api/*.ts. Здесь он внутри экрана намеренно: раздел выходит
// одним файлом, и пока владелец не решил про часы, у него нет и второго потребителя.
// Появится — выносится целиком, менять при этом нечего.
//
// ЧИСЛА НА ЭКРАНЕ
// Ни одного вбитого: и счётчики, и тексты оговорок считает то же ядро, что отдаёт данные
// рядом. Оговорка с цифрой, написанной руками, читается как измерение, а является
// снимком неизвестной давности.

import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from '../api/client'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtMoney, pluralN } from '../lib/format'

// ───────────────────────────── транспорт ─────────────────────────────

const БАЗА = '/api/avr-parsed'

/** Сколько строк на странице. То же число, что у соседних разделов зеркала. */
const РАЗМЕР_СТРАНИЦЫ = 50

/**
 * Тот же разбор ответа, что в api/client.ts и api/coordProjects.ts. Продублирован
 * намеренно: общего «api/base.ts» в проекте нет, и вводить его ради двадцати строк
 * значило бы поставить всем разделам общую точку отказа.
 */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 403) {
      throw new Error('раздел «Разборы актов АВР» вам не открыт: он показывает свод по '
        + 'всем судам сразу и зоной ответственности не сужается')
    }
    // 400 от ядра — это неверный фильтр. Пустая таблица читалась бы как «таких актов
    // нет», а это ложь про выполненную работу.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

interface Список<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
}

interface Разбор {
  doc_key: string
  doc_seq: number
  parses: number
  is_last: boolean
  parsed_at: string | null
  act_number: string | null
  act_number_ok: boolean | null
  act_date: string | null
  act_date_raw: string | null
  vessel_name: string | null
  vessel_flag: string | null
  vessel_port: string | null
  vessel_imo: string | null
  vessel_imo_raw: string | null
  imo_valid: boolean | null
  imo_is_mmsi: boolean | null
  vessel_mmsi: string | null
  customer: string | null
  equipment: string | null
  hours_mismatch: boolean | null
  works_count: number | null
  delivery_count: number | null
  repair_count: number | null
  billing_items_count: number | null
  billing_legacy: boolean | null
}

interface РазборПодробно extends Разбор {
  issue: string | null
  defect_check: string | null
  conclusion: string | null
  customer_letter: string | null
}

/** Шапка документа, приезжающая вместе со строкой состава. */
interface Шапка {
  doc_key: string
  doc_seq: number
  act_number: string | null
  act_number_ok: boolean | null
  act_date: string | null
  vessel_name: string | null
  vessel_imo: string | null
  imo_valid: boolean | null
  customer: string | null
  parsed_at: string | null
}

interface Работа extends Шапка {
  line_no: number
  work_text: string | null
}

interface Позиция extends Шапка {
  line_no: number
  item_name: string | null
  qty: number | null
  unit: string | null
  price: number | null
  line_total: number | null
  kind: string | null
  vat: number | null
  catalog_key: string | null
  comment: string | null
  section: string | null
  item_day: string | null
  item_type: string | null
  is_repair: boolean | null
}

interface Компонент extends Шапка {
  line_no: number
  device: string | null
  component: string | null
  action: string | null
  category: string | null
  market_price_estimate: number | null
  labor_share_pct: number | null
  labor_cost: number | null
  in_office: boolean | null
  comment: string | null
}

interface Поставка {
  line_no: number
  item_name: string | null
  from_board: number | null
  performer: number | null
  customer_qty: number | null
  returned: number | null
  comment: string | null
}

interface Группа {
  doc_key: string
  parses: number
  last_seq: number
  first_parsed_at: string | null
  last_parsed_at: string | null
  act_date: string | null
  vessel_name: string | null
  act_number: string | null
  hours_mismatch: boolean | null
  works_min: number | null
  works_max: number | null
  billing_min: number | null
  billing_max: number | null
}

interface Свежесть {
  entity: string
  last_sync_at: string | null
  last_count: number | null
  age_min: number | null
}

interface Справочники {
  source: string
  counts: Record<string, number>
  freshness: Свежесть[]
  flags: Array<{ flag: string; n: number }>
  ports: Array<{ port: string; n: number }>
  billing_kinds: Array<{ kind: string; n: number }>
  billing_units: Array<{ unit: string; n: number }>
  billing_sections: Array<{ section: string; n: number }>
  repair_categories: Array<{ category: string; n: number }>
  kind_names: Record<string, string>
  section_names: Record<string, string>
  category_names: Record<string, string>
  sort_list: string[]
  closed_fields: { what: string[]; why: string }
  data_notes: Record<string, string>
  caveats: string[]
}

interface Карточка {
  doc: РазборПодробно
  works: Работа[]
  delivery: Поставка[]
  repair: Компонент[]
  billing_items: Позиция[]
  parses: Array<{
    doc_seq: number
    parsed_at: string | null
    works_count: number | null
    billing_items_count: number | null
  }>
}

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

function useСправочники() {
  return useQuery<Справочники>({
    queryKey: ['avr-parsed', 'refs'],
    queryFn: () => запрос<Справочники>('/refs'),
    ...ОБЩЕЕ,
  })
}

function useРазборы(ф: Record<string, unknown>) {
  return useQuery<Список<Разбор> & { totals: Record<string, number> }>({
    queryKey: ['avr-parsed', 'docs', ф],
    queryFn: () => запрос('/docs' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

function useРаботы(ф: Record<string, unknown>) {
  return useQuery<Список<Работа> & { totals: Record<string, number> }>({
    queryKey: ['avr-parsed', 'works', ф],
    queryFn: () => запрос('/works' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

function usePositions(ф: Record<string, unknown>) {
  return useQuery<Список<Позиция> & { totals: Record<string, number> }>({
    queryKey: ['avr-parsed', 'billing', ф],
    queryFn: () => запрос('/billing' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

function useРемонт(ф: Record<string, unknown>) {
  return useQuery<Список<Компонент> & { totals: Record<string, number> }>({
    queryKey: ['avr-parsed', 'repair', ф],
    queryFn: () => запрос('/repair' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

function useПовторы(ф: Record<string, unknown>) {
  return useQuery<Список<Группа> & { totals: Record<string, number> }>({
    queryKey: ['avr-parsed', 'dups', ф],
    queryFn: () => запрос('/dups' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

/** Один разбор целиком. Ключ неполон — не ходим вовсе, а не спрашиваем «ничего». */
function useРазбор(doc?: string | null, seq?: string | null) {
  return useQuery<Карточка>({
    queryKey: ['avr-parsed', 'doc', doc, seq],
    queryFn: () => запрос(`/doc?doc=${encodeURIComponent(doc ?? '')}&seq=${seq ?? ''}`),
    enabled: !!doc && !!seq,
    ...ОБЩЕЕ,
  })
}

// ───────────────────────────── мелкая помощь ─────────────────────────────

/** Название по коду. Незнакомый код показываем как есть, а не подменяем похожим. */
function поКоду(словарь: Record<string, string> | undefined, код?: string | null): string {
  if (!код) return '—'
  return словарь?.[код] ?? код
}

/** Число или прочерк. Пусто здесь — это «в разборе нет», а не ноль. */
function числоИли(v?: number | null): string {
  return v === null || v === undefined ? '—' : String(v)
}

function деньгиИли(v?: number | null): string {
  return v === null || v === undefined ? '—' : fmtMoney(v)
}

/** Ссылка на один разбор. Ключ снаружи — пара «документ + номер разбора». */
function ссылка(doc: string, seq: number): string {
  return `?doc=${encodeURIComponent(doc)}&seq=${seq}`
}

const ВКЛАДКИ = [
  ['docs', 'Разборы'],
  ['works', 'Состав работ'],
  ['billing', 'Позиции счёта'],
  ['repair', 'Компонентный ремонт'],
  ['dups', 'Повторные загрузки'],
] as const

type Вкладка = (typeof ВКЛАДКИ)[number][0]

// ───────────────────────────── экран ─────────────────────────────

export default function AvrParsed() {
  const [параметры] = useSearchParams()
  const doc = параметры.get('doc')
  const seq = параметры.get('seq')
  const [вкладка, setВкладка] = useState<Вкладка>('docs')
  const refs = useСправочники()
  const старейшая = самаяСтарая(refs.data?.freshness)

  return (
    <>
      <div className="page-head">
        <h1>Разборы актов АВР</h1>
        <span className="spacer" />
        <span className="hint">
          {старейшая
            ? `обновлено ${(старейшая.age_min ?? 0) < 1
              ? 'только что'
              : `${Math.round(старейшая.age_min ?? 0)} мин назад`}`
            : 'свежесть неизвестна'}
        </span>
      </div>

      {/* Оговорка про закрытые поля стоит выше данных намеренно: человек, увидевший
          таблицу без часов, сперва решит, что часы потеряли при переносе. */}
      <div className="notice">
        <b>Часы и исполнители в разделе не показываются.</b>{' '}
        В разборах есть и ФИО инженера, и логин загрузившего, и отработанное по датам с
        разбивкой день/вечер/ночь. Это тот же разряд данных, что овертаймы, и решение о их
        видимости за владельцем — оно ещё не принято. Данные не потеряны: они лежат в
        зеркале, но ядро их не выбирает, поэтому включить их здесь нечем.
      </div>

      {doc && seq ? (
        <ОдинРазбор doc={doc} seq={seq} refs={refs.data} />
      ) : (
        <>
          {/* Вкладки — обычный flex, а НЕ .filters: на телефоне правило
              «.filters > * { width: 100% }» поставило бы кнопки в столбец. */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '12px 0' }}>
            {ВКЛАДКИ.map(([код, подпись]) => (
              <button
                key={код}
                onClick={() => setВкладка(код)}
                aria-pressed={вкладка === код}
                style={{ fontWeight: вкладка === код ? 600 : 400, flex: '0 1 auto' }}
              >
                {подпись}
              </button>
            ))}
          </div>

          <QueryState isLoading={refs.isLoading} error={refs.error}>
            {вкладка === 'docs' && <Разборы refs={refs.data} />}
            {вкладка === 'works' && <Работы />}
            {вкладка === 'billing' && <Позиции refs={refs.data} />}
            {вкладка === 'repair' && <Ремонт refs={refs.data} />}
            {вкладка === 'dups' && <Повторы />}
            <Оговорки refs={refs.data} />
          </QueryState>
        </>
      )}
    </>
  )
}

/** Самая отставшая сущность зеркала: свежесть раздела — по худшей, а не по лучшей. */
function самаяСтарая(список?: Свежесть[]): Свежесть | undefined {
  if (!список?.length) return undefined
  return [...список].sort((a, b) => (b.age_min ?? 0) - (a.age_min ?? 0))[0]
}

/** Оговорки из ядра. Тексты и числа в них считает то же ядро, что и данные рядом. */
function Оговорки({ refs }: { refs?: Справочники }) {
  const тексты = refs?.caveats ?? []
  if (!тексты.length) return null
  return (
    <div className="card hint" style={{ marginTop: 12 }}>
      <b>Что нужно знать об этих данных</b>
      <ul className="plain-list" style={{ marginTop: 6 }}>
        {тексты.map((т) => <li key={т.slice(0, 40)}>{т}</li>)}
      </ul>
    </div>
  )
}

function Страницы({ всего, start, next, setStart }: {
  всего: number; start: number; next: number | null; setStart: (v: number) => void
}) {
  if (всего <= РАЗМЕР_СТРАНИЦЫ) return null
  return (
    <div className="pager">
      <button
        disabled={start === 0}
        onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}
      >
        ← назад
      </button>
      <span className="muted">
        {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, всего)} из {всего}
      </span>
      <button disabled={next === null} onClick={() => setStart(next ?? start)}>
        вперёд →
      </button>
    </div>
  )
}

/** Пусто — это ответ про отбор, а не про работу. Так и говорим. */
function Пусто({ заголовок, подсказка }: { заголовок: string; подсказка: string }) {
  return (
    <div className="card empty">
      <div className="empty-title">{заголовок}</div>
      <div className="empty-hint">{подсказка}</div>
    </div>
  )
}

/** Номер акта и признак его негодности рядом. Значение показывается как есть. */
function Акт({ номер, годен }: { номер?: string | null; годен?: boolean | null }) {
  if (годен) return <>{номер}</>
  return (
    <>
      <span className="mono">{номер ? номер : '—'}</span>
      <div className="hint">номер не опознан — так в источнике</div>
    </>
  )
}

/** IMO: сырое значение и почему по нему не найдётся судно. */
function Имо({ r }: { r: { vessel_imo: string | null; vessel_imo_raw?: string | null;
  imo_valid: boolean | null; imo_is_mmsi?: boolean | null } }) {
  if (r.imo_valid) return <span className="mono">{r.vessel_imo}</span>
  return (
    <>
      <span className="mono">{r.vessel_imo_raw ? r.vessel_imo_raw : '—'}</span>
      <div className="hint">
        {r.imo_is_mmsi
          ? 'в поле IMO стоит девятизначное число — судно по нему не ищется'
          : 'контрольная сумма не сошлась — судно по нему не ищется'}
      </div>
    </>
  )
}

// ───────────────────────────── вкладка «Разборы» ─────────────────────────────

function Разборы({ refs }: { refs?: Справочники }) {
  const [поиск, setПоиск] = useState('')
  const [флаг, setФлаг] = useState('')
  const [толькоПоследний, setТолькоПоследний] = useState(false)
  const [имоПлохой, setИмоПлохой] = useState(false)
  const [актПлохой, setАктПлохой] = useState(false)
  const [расхождение, setРасхождение] = useState(false)
  const [start, setStart] = useState(0)

  const фильтр = useMemo(() => ({
    q: поиск,
    flag: флаг,
    last: толькоПоследний ? '1' : '',
    imo_ok: имоПлохой ? '0' : '',
    act_ok: актПлохой ? '0' : '',
    mismatch: расхождение ? '1' : '',
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [поиск, флаг, толькоПоследний, имоПлохой, актПлохой, расхождение, start])

  const запр = useРазборы(фильтр)
  const строки = запр.data?.rows ?? []
  const итоги = запр.data?.totals
  const сСброса = <T,>(f: (v: T) => void) => (v: T) => { setStart(0); f(v) }

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="avr-parsed-docs">
          <div className="f">
            <label htmlFor="avr-docs-q">Поиск</label>
            <input
              id="avr-docs-q"
              value={поиск}
              onChange={(e) => сСброса(setПоиск)(e.target.value)}
              placeholder="судно, номер акта, заказчик, оборудование"
            />
          </div>
          <div className="f">
            <label htmlFor="avr-docs-flag">Флаг</label>
            <select
              id="avr-docs-flag"
              value={флаг}
              onChange={(e) => сСброса(setФлаг)(e.target.value)}
            >
              <option value="">все</option>
              {(refs?.flags ?? []).map((ф) => (
                <option key={ф.flag} value={ф.flag}>{ф.flag}</option>
              ))}
            </select>
          </div>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={толькоПоследний}
              onChange={(e) => сСброса(setТолькоПоследний)(e.target.checked)}
            />
            только последний разбор документа
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={имоПлохой}
              onChange={(e) => сСброса(setИмоПлохой)(e.target.checked)}
            />
            IMO негоден
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={актПлохой}
              onChange={(e) => сСброса(setАктПлохой)(e.target.checked)}
            />
            номер акта негоден
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={расхождение}
              onChange={(e) => сСброса(setРасхождение)(e.target.checked)}
            />
            итог часов не сошёлся
          </label>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          найдено: {запр.data?.total ?? 0}
          {итоги
            ? ` · ${pluralN(итоги.documents ?? 0, 'документ', 'документа', 'документов')}`
            : ''}
        </div>
      </div>

      <QueryState isLoading={запр.isLoading} error={запр.error}>
        {итоги && <ПлиткиРазборов итоги={итоги} />}
        {строки.length === 0 ? (
          <Пусто
            заголовок="Под этот отбор не попал ни один разбор"
            подсказка={'Это ответ про фильтр, а не про работу: снимите поиск или отметки '
              + '«IMO негоден» и «номер акта негоден». Новые разборы приезжают в зеркало '
              + 'ближайшим прогоном.'}
          />
        ) : (
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Акт</th>
                  <th>Судно</th>
                  <th>IMO</th>
                  <th>Заказчик</th>
                  <th>Оборудование</th>
                  <th className="num">Работ</th>
                  <th className="num">Позиций</th>
                  <th>Разбор</th>
                </tr>
              </thead>
              <tbody>
                {строки.map((r) => (
                  <tr key={`${r.doc_key}#${r.doc_seq}`}>
                    <td data-label="Акт">
                      <Link to={ссылка(r.doc_key, r.doc_seq)}>
                        <Акт номер={r.act_number} годен={r.act_number_ok} />
                      </Link>
                      <div className="hint">{fmtDate(r.act_date)}</div>
                    </td>
                    <td data-label="Судно">
                      {r.vessel_name ? r.vessel_name : <span className="hint">не назван</span>}
                      <div className="hint">{r.vessel_flag} · {r.vessel_port}</div>
                    </td>
                    <td data-label="IMO"><Имо r={r} /></td>
                    <td data-label="Заказчик">
                      {r.customer
                        ? r.customer
                        : <span className="hint">в разборе не указан</span>}
                    </td>
                    <td data-label="Оборудование">{r.equipment}</td>
                    <td className="num" data-label="Работ">{числоИли(r.works_count)}</td>
                    <td className="num" data-label="Позиций">
                      {r.billing_items_count
                        ? r.billing_items_count
                        : <span className="hint">счёт только целиком</span>}
                    </td>
                    <td data-label="Разбор">
                      <div>{fmtDate(r.parsed_at, true)}</div>
                      {r.parses > 1 && (
                        <div className="hint">
                          {pluralN(r.parses, 'разбор', 'разбора', 'разборов')} этого
                          документа, здесь {r.doc_seq}-й{r.is_last ? ' (последний)' : ''}
                        </div>
                      )}
                      {r.hours_mismatch && (
                        <div className="hint">итог часов не сошёлся с суммой по дням</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Страницы
          всего={запр.data?.total ?? 0}
          start={start}
          next={запр.data?.next ?? null}
          setStart={setStart}
        />
      </QueryState>
    </>
  )
}

function ПлиткиРазборов({ итоги }: { итоги: Record<string, number> }) {
  return (
    <div className="tiles" style={{ marginBottom: 12 }}>
      <div className="tile">
        <div className="t-label">разборов под отбором</div>
        <div className="t-sum">{итоги.parses ?? 0}</div>
        <div className="t-label">
          документов: {итоги.documents ?? 0} · повторно грузили: {итоги.in_groups ?? 0}
        </div>
      </div>
      <div className="tile">
        <div className="t-label">IMO негоден</div>
        <div className="t-sum">{итоги.imo_bad ?? 0}</div>
        <div className="t-label">из них девятизначных: {итоги.imo_mmsi ?? 0}</div>
      </div>
      <div className="tile">
        <div className="t-label">номер акта негоден</div>
        <div className="t-sum">{итоги.act_bad ?? 0}</div>
        <div className="t-label">итог часов не сошёлся: {итоги.mismatch ?? 0}</div>
      </div>
      <div className="tile">
        <div className="t-label">строк состава работ</div>
        <div className="t-sum">{итоги.works ?? 0}</div>
        <div className="t-label">
          позиций счёта: {итоги.billing_items ?? 0} · ремонта: {итоги.repair ?? 0}
        </div>
      </div>
    </div>
  )
}

// ───────────────────────────── вкладка «Состав работ» ─────────────────────────────

function Работы() {
  const [поиск, setПоиск] = useState('')
  const [start, setStart] = useState(0)
  const фильтр = useMemo(
    () => ({ q: поиск, start, limit: РАЗМЕР_СТРАНИЦЫ }),
    [поиск, start],
  )
  const запр = useРаботы(фильтр)
  const строки = запр.data?.rows ?? []
  const итоги = запр.data?.totals

  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <FilterBar id="avr-parsed-works">
          <div className="f">
            <label htmlFor="avr-works-q">Что делали</label>
            <input
              id="avr-works-q"
              value={поиск}
              onChange={(e) => { setStart(0); setПоиск(e.target.value) }}
              placeholder="например: следящая система"
            />
          </div>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          найдено: {запр.data?.total ?? 0}
          {итоги
            ? ` · ${pluralN(итоги.documents ?? 0, 'документ', 'документа', 'документов')}`
              + ` · ${pluralN(итоги.vessels ?? 0, 'судно', 'судна', 'судов')}`
            : ''}
        </div>
      </div>

      <QueryState isLoading={запр.isLoading} error={запр.error}>
        {строки.length === 0 ? (
          <Пусто
            заголовок="Таких строк работ не нашлось"
            подсказка={'Поиск идёт по тексту работ, а не по разбору целиком: по всему '
              + 'тексту искать нельзя, подбором строки так подтверждается закрытое поле. '
              + 'Попробуйте другое слово или его часть.'}
          />
        ) : (
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Что делали</th>
                  <th>Судно</th>
                  <th>Акт</th>
                  <th className="num">Строка</th>
                </tr>
              </thead>
              <tbody>
                {строки.map((r) => (
                  <tr key={`${r.doc_key}#${r.doc_seq}#${r.line_no}`}>
                    <td data-label="Что делали">{r.work_text}</td>
                    <td data-label="Судно">
                      {r.vessel_name}
                      {!r.imo_valid && <div className="hint">IMO негоден</div>}
                    </td>
                    <td data-label="Акт">
                      <Link to={ссылка(r.doc_key, r.doc_seq)}>
                        <Акт номер={r.act_number} годен={r.act_number_ok} />
                      </Link>
                      <div className="hint">{fmtDate(r.act_date)}</div>
                    </td>
                    <td className="num" data-label="Строка">{r.line_no}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Страницы
          всего={запр.data?.total ?? 0}
          start={start}
          next={запр.data?.next ?? null}
          setStart={setStart}
        />
      </QueryState>
    </>
  )
}

// ───────────────────────────── вкладка «Позиции счёта» ─────────────────────────────

function Позиции({ refs }: { refs?: Справочники }) {
  const [поиск, setПоиск] = useState('')
  const [вид, setВид] = useState('')
  const [раздел, setРаздел] = useState('')
  const [start, setStart] = useState(0)
  const фильтр = useMemo(
    () => ({ q: поиск, kind: вид, section: раздел, start, limit: РАЗМЕР_СТРАНИЦЫ }),
    [поиск, вид, раздел, start],
  )
  const запр = usePositions(фильтр)
  const строки = запр.data?.rows ?? []
  const итоги = запр.data?.totals

  return (
    <>
      <div className="notice">
        <b>Это не выставленный счёт.</b>{' '}
        Позиции — предложение разбора, из которого счёт собирали руками, и уйти заказчику
        мог другой. Сводить эти суммы с 1С как выручку нельзя; сверять с проведёнными
        документами — можно.
      </div>

      <div className="card" style={{ margin: '12px 0' }}>
        <FilterBar id="avr-parsed-billing">
          <div className="f">
            <label htmlFor="avr-billing-q">Поиск</label>
            <input
              id="avr-billing-q"
              value={поиск}
              onChange={(e) => { setStart(0); setПоиск(e.target.value) }}
              placeholder="наименование, комментарий, тип оборудования"
            />
          </div>
          <div className="f">
            <label htmlFor="avr-billing-kind">Вид</label>
            <select
              id="avr-billing-kind"
              value={вид}
              onChange={(e) => { setStart(0); setВид(e.target.value) }}
            >
              <option value="">все</option>
              {(refs?.billing_kinds ?? []).filter((к) => к.kind).map((к) => (
                <option key={к.kind} value={к.kind}>
                  {поКоду(refs?.kind_names, к.kind)}
                </option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="avr-billing-section">Раздел</label>
            <select
              id="avr-billing-section"
              value={раздел}
              onChange={(e) => { setStart(0); setРаздел(e.target.value) }}
            >
              <option value="">все</option>
              {(refs?.billing_sections ?? []).filter((с) => с.section).map((с) => (
                <option key={с.section} value={с.section}>
                  {поКоду(refs?.section_names, с.section)}
                </option>
              ))}
            </select>
          </div>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          найдено: {запр.data?.total ?? 0}
          {итоги
            ? ` · ${pluralN(итоги.documents ?? 0, 'документ', 'документа', 'документов')}`
              + ` · на ${деньгиИли(итоги.line_total)}`
            : ''}
        </div>
      </div>

      <QueryState isLoading={запр.isLoading} error={запр.error}>
        {строки.length === 0 ? (
          <Пусто
            заголовок="Под этот отбор не попала ни одна позиция"
            подсказка={'Построчный счёт есть не у всех разборов: у части он лежит в '
              + 'источнике одним куском в шести разных разрезах одних и тех же денег, и '
              + 'раскладывать его в строки нельзя — сумма вышла бы вшестеро больше.'}
          />
        ) : (
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Позиция</th>
                  <th>Судно и акт</th>
                  <th className="num">Кол-во</th>
                  <th className="num">Цена</th>
                  <th className="num">Сумма</th>
                  <th>Вид</th>
                </tr>
              </thead>
              <tbody>
                {строки.map((r) => (
                  <tr key={`${r.doc_key}#${r.doc_seq}#${r.line_no}`}>
                    <td data-label="Позиция">
                      {r.item_name}
                      {r.comment && <div className="hint">{r.comment}</div>}
                    </td>
                    <td data-label="Судно и акт">
                      <div>{r.vessel_name}</div>
                      <Link to={ссылка(r.doc_key, r.doc_seq)} className="hint">
                        {r.act_number_ok ? r.act_number : 'акт без номера'}
                      </Link>
                    </td>
                    <td className="num" data-label="Кол-во">
                      {числоИли(r.qty)} {r.unit}
                    </td>
                    <td className="num" data-label="Цена">{деньгиИли(r.price)}</td>
                    <td className="num" data-label="Сумма">
                      {r.line_total === null
                        ? <span className="hint">не посчитана</span>
                        : fmtMoney(r.line_total)}
                    </td>
                    <td data-label="Вид">
                      {поКоду(refs?.kind_names, r.kind)}
                      {r.section && (
                        <div className="hint">{поКоду(refs?.section_names, r.section)}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Страницы
          всего={запр.data?.total ?? 0}
          start={start}
          next={запр.data?.next ?? null}
          setStart={setStart}
        />
      </QueryState>
    </>
  )
}

// ───────────────────────────── вкладка «Ремонт» ─────────────────────────────

function Ремонт({ refs }: { refs?: Справочники }) {
  const [поиск, setПоиск] = useState('')
  const [вид, setВид] = useState('')
  const [start, setStart] = useState(0)
  const фильтр = useMemo(
    () => ({ q: поиск, category: вид, start, limit: РАЗМЕР_СТРАНИЦЫ }),
    [поиск, вид, start],
  )
  const запр = useРемонт(фильтр)
  const строки = запр.data?.rows ?? []
  const итоги = запр.data?.totals

  return (
    <>
      <div className="notice">
        <b>Цена здесь — оценка модели в момент разбора,</b> а не прайс и не выплата.
        Сверять её с прайсом полезно, считать по ней деньги — нет.
      </div>

      <div className="card" style={{ margin: '12px 0' }}>
        <FilterBar id="avr-parsed-repair">
          <div className="f">
            <label htmlFor="avr-repair-q">Поиск</label>
            <input
              id="avr-repair-q"
              value={поиск}
              onChange={(e) => { setStart(0); setПоиск(e.target.value) }}
              placeholder="прибор, узел, что делали"
            />
          </div>
          <div className="f">
            <label htmlFor="avr-repair-cat">Вид ремонта</label>
            <select
              id="avr-repair-cat"
              value={вид}
              onChange={(e) => { setStart(0); setВид(e.target.value) }}
            >
              <option value="">все</option>
              {(refs?.repair_categories ?? []).filter((к) => к.category).map((к) => (
                <option key={к.category} value={к.category}>
                  {поКоду(refs?.category_names, к.category)}
                </option>
              ))}
            </select>
          </div>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          найдено: {запр.data?.total ?? 0}
          {итоги
            ? ` · ${pluralN(итоги.documents ?? 0, 'документ', 'документа', 'документов')}`
              + ` · оценка компонентов ${деньгиИли(итоги.market_price_estimate)}`
            : ''}
        </div>
      </div>

      <QueryState isLoading={запр.isLoading} error={запр.error}>
        {строки.length === 0 ? (
          <Пусто
            заголовок="Компонентного ремонта под этот отбор нет"
            подсказка={'Ремонт разбирается построчно далеко не в каждом акте — чаще всего '
              + 'его в акте просто нет. Снимите вид ремонта или поиск.'}
          />
        ) : (
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Прибор и узел</th>
                  <th>Что делали</th>
                  <th>Судно и акт</th>
                  <th className="num">Оценка компонента</th>
                  <th className="num">Работа</th>
                  <th>В офисе</th>
                </tr>
              </thead>
              <tbody>
                {строки.map((r) => (
                  <tr key={`${r.doc_key}#${r.doc_seq}#${r.line_no}`}>
                    <td data-label="Прибор и узел">
                      {r.device}
                      <div className="hint">{r.component}</div>
                    </td>
                    <td data-label="Что делали">
                      {r.action}
                      <div className="hint">{поКоду(refs?.category_names, r.category)}</div>
                    </td>
                    <td data-label="Судно и акт">
                      <div>{r.vessel_name}</div>
                      <Link to={ссылка(r.doc_key, r.doc_seq)} className="hint">
                        {r.act_number_ok ? r.act_number : 'акт без номера'}
                      </Link>
                    </td>
                    <td className="num" data-label="Оценка компонента">
                      {деньгиИли(r.market_price_estimate)}
                    </td>
                    <td className="num" data-label="Работа">
                      {деньгиИли(r.labor_cost)}
                      {r.labor_share_pct !== null && (
                        <div className="hint">доля {r.labor_share_pct} %</div>
                      )}
                    </td>
                    <td data-label="В офисе">
                      {r.in_office === null
                        ? <span className="hint">не сказано</span>
                        : (r.in_office ? 'есть' : 'нет')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Страницы
          всего={запр.data?.total ?? 0}
          start={start}
          next={запр.data?.next ?? null}
          setStart={setStart}
        />
      </QueryState>
    </>
  )
}

// ─────────────────────── вкладка «Повторные загрузки» ───────────────────────

function Повторы() {
  const [все, setВсе] = useState(false)
  const [start, setStart] = useState(0)
  const фильтр = useMemo(
    () => ({ all: все ? '1' : '', start, limit: РАЗМЕР_СТРАНИЦЫ }),
    [все, start],
  )
  const запр = useПовторы(фильтр)
  const строки = запр.data?.rows ?? []
  const итоги = запр.data?.totals

  return (
    <>
      <div className="notice">
        <b>Повтор — не мусор.</b>{' '}
        Один и тот же акт грузили в разбор по нескольку раз, и разборы у него разные:
        модель на том же файле отвечает по-разному. Раздел показывает группы, а не
        выбирает победителя за человека — и ничего не удаляет.
      </div>

      <div className="card" style={{ margin: '12px 0' }}>
        <FilterBar id="avr-parsed-dups">
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={все}
              onChange={(e) => { setStart(0); setВсе(e.target.checked) }}
            />
            показать и документы с единственным разбором
          </label>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          найдено: {запр.data?.total ?? 0}
          {итоги
            ? ` · лишних загрузок: ${итоги.extra ?? 0}`
            : ''}
        </div>
      </div>

      <QueryState isLoading={запр.isLoading} error={запр.error}>
        {строки.length === 0 ? (
          <Пусто
            заголовок="Повторных загрузок не нашлось"
            подсказка={'Документ считается тем же, если совпали номер акта, судно и дата '
              + 'акта. Отметьте «показать и документы с единственным разбором», чтобы '
              + 'увидеть весь список.'}
          />
        ) : (
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Документ</th>
                  <th className="num">Разборов</th>
                  <th>Когда разбирали</th>
                  <th className="num">Строк работ</th>
                  <th className="num">Позиций счёта</th>
                </tr>
              </thead>
              <tbody>
                {строки.map((г) => (
                  <tr key={г.doc_key}>
                    <td data-label="Документ">
                      <Link to={ссылка(г.doc_key, г.last_seq)}>
                        {г.vessel_name ? г.vessel_name : 'судно не названо'}
                      </Link>
                      <div className="hint">
                        {г.act_number ? г.act_number : 'акт без номера'} · {fmtDate(г.act_date)}
                      </div>
                    </td>
                    <td className="num" data-label="Разборов">{г.parses}</td>
                    <td data-label="Когда разбирали">
                      <div>{fmtDate(г.first_parsed_at, true)}</div>
                      <div className="hint">последний: {fmtDate(г.last_parsed_at, true)}</div>
                    </td>
                    <td className="num" data-label="Строк работ">
                      {г.works_min === г.works_max
                        ? числоИли(г.works_max)
                        : `${числоИли(г.works_min)} … ${числоИли(г.works_max)}`}
                    </td>
                    <td className="num" data-label="Позиций счёта">
                      {г.billing_min === г.billing_max
                        ? числоИли(г.billing_max)
                        : `${числоИли(г.billing_min)} … ${числоИли(г.billing_max)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Страницы
          всего={запр.data?.total ?? 0}
          start={start}
          next={запр.data?.next ?? null}
          setStart={setStart}
        />
      </QueryState>
    </>
  )
}

// ───────────────────────────── один разбор ─────────────────────────────

function ОдинРазбор({ doc, seq, refs }: { doc: string; seq: string; refs?: Справочники }) {
  const запр = useРазбор(doc, seq)
  const к = запр.data

  return (
    <>
      <div style={{ margin: '12px 0' }}>
        <Link to="?">← ко всем разборам</Link>
      </div>

      <QueryState isLoading={запр.isLoading} error={запр.error}>
        {!к ? (
          <Пусто
            заголовок="Такого разбора нет"
            подсказка={'Разбор опознаётся парой «документ и номер разбора»: один акт '
              + 'грузили по нескольку раз, и у каждой загрузки свой номер, начиная с '
              + 'первого.'}
          />
        ) : (
          <>
            <Шапка_ d={к.doc} />
            <Тексты d={к.doc} />
            <СоставРабот строки={к.works} />
            <Поставки строки={к.delivery} />
            <РемонтРазбора строки={к.repair} refs={refs} />
            <ПозицииРазбора строки={к.billing_items} d={к.doc} refs={refs} />
            <ДругиеРазборы список={к.parses} doc={doc} текущий={к.doc.doc_seq} />
          </>
        )}
        <Оговорки refs={refs} />
      </QueryState>
    </>
  )
}

function Шапка_({ d }: { d: РазборПодробно }) {
  return (
    <div className="card">
      <h2>{d.vessel_name ? d.vessel_name : 'Судно не названо'}</h2>
      <div className="dl">
        <div>Акт</div>
        <div><Акт номер={d.act_number} годен={d.act_number_ok} /></div>
        <div>Дата акта</div>
        <div>
          {fmtDate(d.act_date)}
          {!d.act_date && d.act_date_raw && (
            <div className="hint">в источнике: {d.act_date_raw}</div>
          )}
        </div>
        <div>IMO</div>
        <div><Имо r={d} /></div>
        <div>MMSI</div>
        <div className="mono">{d.vessel_mmsi ? d.vessel_mmsi : '—'}</div>
        <div>Флаг и порт</div>
        <div>{d.vessel_flag} · {d.vessel_port}</div>
        <div>Заказчик</div>
        <div>{d.customer ? d.customer : <span className="hint">в разборе не указан</span>}</div>
        <div>Оборудование</div>
        <div>{d.equipment}</div>
        <div>Когда разбирали</div>
        <div>
          {fmtDate(d.parsed_at, true)}
          {d.parses > 1 && (
            <div className="hint">
              это {d.doc_seq}-й из {pluralN(d.parses, 'разбора', 'разборов', 'разборов')}
              {d.is_last ? ', последний' : ''}
            </div>
          )}
        </div>
      </div>
      {d.hours_mismatch && (
        <div className="notice">
          Итог часов в этом разборе не сошёлся с суммой по дням. Расхождение показано
          признаком; сами часы раздел не показывает — решение о их видимости за владельцем.
        </div>
      )}
      {d.billing_legacy && !d.billing_items_count && (
        <div className="notice">
          Счёт у этого разбора лежит в источнике одним куском в шести разных разрезах
          одних и тех же денег. Разложить его в строки нельзя: сумма вышла бы вшестеро
          больше настоящей, — поэтому позиций ниже не будет.
        </div>
      )}
    </div>
  )
}

function Тексты({ d }: { d: РазборПодробно }) {
  const блоки: Array<[string, string | null]> = [
    ['Неисправность', d.issue],
    ['Дефектация', d.defect_check],
    ['Заключение', d.conclusion],
    ['Письмо заказчику', d.customer_letter],
  ]
  const есть = блоки.filter(([, т]) => !!т)
  if (!есть.length) return null
  return (
    <div className="card">
      {есть.map(([имя, текст]) => (
        <div key={имя} style={{ marginBottom: 10 }}>
          <b>{имя}</b>
          <div style={{ whiteSpace: 'pre-wrap' }}>{текст}</div>
        </div>
      ))}
      <div className="hint">
        Пояснение к счёту — как именно его посчитали — здесь не показано: это свободный
        текст, в котором расписаны смены по часам, а местами названа фамилия инженера.
      </div>
    </div>
  )
}

function СоставРабот({ строки }: { строки: Работа[] }) {
  return (
    <div className="card">
      <h2>Состав работ</h2>
      {строки.length === 0 ? (
        <div className="empty">
          <div className="empty-title">Состав работ в этом разборе не разобран</div>
          <div className="empty-hint">
            Так в источнике: модель не выделила из акта ни одной строки. У других разборов
            того же документа состав может быть — посмотрите их ниже.
          </div>
        </div>
      ) : (
        <div className="table-wrap cards">
          <table>
            <thead>
              <tr>
                <th className="num">№</th>
                <th>Что делали</th>
              </tr>
            </thead>
            <tbody>
              {строки.map((р) => (
                <tr key={р.line_no}>
                  <td className="num" data-label="№">{р.line_no}</td>
                  <td data-label="Что делали">{р.work_text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Поставки({ строки }: { строки: Поставка[] }) {
  if (!строки.length) return null
  return (
    <div className="card">
      <h2>Передано на борт и с борта</h2>
      <div className="hint">Числа здесь — количества, а не деньги.</div>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Что</th>
              <th className="num">С борта</th>
              <th className="num">От исполнителя</th>
              <th className="num">Заказчику</th>
              <th className="num">Возвращено</th>
              <th>Примечание</th>
            </tr>
          </thead>
          <tbody>
            {строки.map((п) => (
              <tr key={п.line_no}>
                <td data-label="Что">{п.item_name}</td>
                <td className="num" data-label="С борта">{числоИли(п.from_board)}</td>
                <td className="num" data-label="От исполнителя">{числоИли(п.performer)}</td>
                <td className="num" data-label="Заказчику">{числоИли(п.customer_qty)}</td>
                <td className="num" data-label="Возвращено">{числоИли(п.returned)}</td>
                <td data-label="Примечание">{п.comment}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function РемонтРазбора({ строки, refs }: { строки: Компонент[]; refs?: Справочники }) {
  if (!строки.length) return null
  return (
    <div className="card">
      <h2>Компонентный ремонт</h2>
      <div className="hint">
        Цена — оценка модели в момент разбора, а не прайс и не выплата.
      </div>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Прибор и узел</th>
              <th>Что делали</th>
              <th className="num">Оценка компонента</th>
              <th className="num">Работа</th>
              <th>В офисе</th>
            </tr>
          </thead>
          <tbody>
            {строки.map((р) => (
              <tr key={р.line_no}>
                <td data-label="Прибор и узел">
                  {р.device}
                  <div className="hint">{р.component}</div>
                </td>
                <td data-label="Что делали">
                  {р.action}
                  <div className="hint">{поКоду(refs?.category_names, р.category)}</div>
                </td>
                <td className="num" data-label="Оценка компонента">
                  {деньгиИли(р.market_price_estimate)}
                </td>
                <td className="num" data-label="Работа">{деньгиИли(р.labor_cost)}</td>
                <td data-label="В офисе">
                  {р.in_office === null
                    ? <span className="hint">не сказано</span>
                    : (р.in_office ? 'есть' : 'нет')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ПозицииРазбора({ строки, d, refs }: {
  строки: Позиция[]; d: РазборПодробно; refs?: Справочники
}) {
  const сумма = строки.reduce((s, п) => s + (п.line_total ?? 0), 0)
  return (
    <div className="card">
      <h2>Позиции счёта</h2>
      {строки.length === 0 ? (
        <div className="empty">
          <div className="empty-title">Построчного счёта у этого разбора нет</div>
          <div className="empty-hint">
            {d.billing_legacy
              ? 'Счёт лежит в источнике одним куском в шести разных разрезах одних и тех '
                + 'же денег: разложить его в строки нельзя, сумма вышла бы вшестеро больше.'
              : 'Счёт в этом разборе не собирали вовсе — так в источнике.'}
          </div>
        </div>
      ) : (
        <>
          <div className="hint">
            Часы в позициях — это то, за что выставлен счёт. Отработанного конкретным
            человеком по датам и сменам здесь нет.
          </div>
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Позиция</th>
                  <th className="num">Кол-во</th>
                  <th className="num">Цена</th>
                  <th className="num">Сумма</th>
                  <th>Вид</th>
                </tr>
              </thead>
              <tbody>
                {строки.map((п) => (
                  <tr key={п.line_no}>
                    <td data-label="Позиция">
                      {п.item_name}
                      {п.comment && <div className="hint">{п.comment}</div>}
                    </td>
                    <td className="num" data-label="Кол-во">{числоИли(п.qty)} {п.unit}</td>
                    <td className="num" data-label="Цена">{деньгиИли(п.price)}</td>
                    <td className="num" data-label="Сумма">
                      {п.line_total === null
                        ? <span className="hint">не посчитана</span>
                        : fmtMoney(п.line_total)}
                    </td>
                    <td data-label="Вид">
                      {поКоду(refs?.kind_names, п.kind)}
                      {п.section && (
                        <div className="hint">{поКоду(refs?.section_names, п.section)}</div>
                      )}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td data-label="">Итого по позициям</td>
                  <td className="num" data-label=""></td>
                  <td className="num" data-label=""></td>
                  <td className="num" data-label="Итого">{fmtMoney(сумма)}</td>
                  <td data-label=""></td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

function ДругиеРазборы({ список, doc, текущий }: {
  список: Карточка['parses']; doc: string; текущий: number
}) {
  if (список.length <= 1) return null
  return (
    <div className="card">
      <h2>Другие разборы этого документа</h2>
      <div className="hint">
        Совпадающих разборов нет ни одного: модель на том же файле отвечает по-разному.
        Какой из них верный — решает человек, а не зеркало.
      </div>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th className="num">Разбор</th>
              <th>Когда</th>
              <th className="num">Строк работ</th>
              <th className="num">Позиций счёта</th>
            </tr>
          </thead>
          <tbody>
            {список.map((р) => (
              <tr key={р.doc_seq}>
                <td className="num" data-label="Разбор">
                  {р.doc_seq === текущий
                    ? <b>{р.doc_seq}</b>
                    : <Link to={ссылка(doc, р.doc_seq)}>{р.doc_seq}</Link>}
                </td>
                <td data-label="Когда">{fmtDate(р.parsed_at, true)}</td>
                <td className="num" data-label="Строк работ">{числоИли(р.works_count)}</td>
                <td className="num" data-label="Позиций счёта">
                  {числоИли(р.billing_items_count)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
